import extend from 'dva-model-extend';
import md5 from 'md5';
import polling from '@kc/gbiz-base/lib/polling';
import QueryPersistence, { queryPersistence } from '@kc/gbiz-base/lib/QueryPersistence';
import {
  CAPTCHA_CODE,
  TOKEN_INVALID_CODE,
  UTM_RCODE_MAP,
  MAIL_AUTHORIZE_EXPIRE_CODE,
} from '../common/constants';
import { ACCOUNTLOGINTABKEY, PHONELOGINTABKEY, ACCOUNT_KEY, NAMESPACE } from './constants';
import {
  parseQuery,
  getJWTPath,
  transformObjWithMap,
  kcsensorsManualTrack,
  getAnonymousID,
  sensorsLogin,
  checkIsNeedMailAuthorize,
} from '../common/tools';
import captchaModel from '../components/HumanCaptcha/model';
import * as services from './service';

/** 跳转jwt，后续的异步逻辑开关，保证只触发一次 */
let JUMP_JWT = false;

function loopCrypto(str, time) {
  const salt = '_kucoin_';
  const c = md5(`${salt}${str}${salt}`).toString();
  if (time <= 0) {
    return c;
  }
  return loopCrypto(c, time - 1);
}

function getUtm() {
  const utm = queryPersistence.getPersistenceQuery(QueryPersistence.UTM_GROUP);

  return transformObjWithMap(utm, UTM_RCODE_MAP);
}

let _loginType = ''; // 登录方式

const initialState = {
  needValidations: [],
  smsRetryAfterSeconds: null,
  accountDup: false,
  type: 1,
  countryCode: '',
  loadingSms: false,
  sendChannel: 'MY_SMS',
  riskTag: '',
  verifyToken: '',
  verifyCheckToken: '',
  isShowMailAuthorizePage: false,
};

export default extend(captchaModel, polling, {
  namespace: NAMESPACE,
  state: {
    loginLoading: false,
    countryCodes: [],
    accountDup: false,
    initToken: undefined,
    loginToken: undefined, // 登录凭证
    status: undefined, // 二维码状态
    ...initialState,
    // needValidations: [['google_2fa'], ['my_sms']],
    gfaBtnLoading: false,
  },
  reducers: {
    update(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
    reset(state) {
      return {
        ...state,
        ...initialState,
      };
    },
  },
  effects: {
    *loginCallback({ payload, onSuccess, trackResultParams = {} }, { put }) {
      const { jwtToken, loginToken, needValidations, type = 1, countryCode = '', ...otherPayload } =
        payload || {};
      const isNeedMailAuthorize = checkIsNeedMailAuthorize(payload);
      const parsed = parseQuery();
      const { return_to } = parsed;
      if (jwtToken) {
        // 直接跳转jwt逻辑
        if (return_to) {
          if (!JUMP_JWT) {
            JUMP_JWT = true;
            window.location.href = getJWTPath('zendesk', jwtToken, return_to);
          }
          return;
        }
      }
      const nextNeedValidations = needValidations || [];

      yield put({
        type: 'update',
        payload: {
          type,
          countryCode,
          token: loginToken,
          loginLoading: false,
          needValidations: nextNeedValidations,
          ...otherPayload,
        },
      });
      if (!isNeedMailAuthorize && !nextNeedValidations.length) {
        yield put({
          type: 'validate',
          payload: {
            validationType: 'EMPTY',
          },
          onSuccess,
          trackResultParams,
        });
      }
    },
    *login({ payload, onSuccess, bizType, trackResultParams = {} }, { call, put }) {
      yield put({ type: 'update', payload: { loginLoading: true } });
      try {
        const { password, loginType = '' } = payload;
        _loginType = loginType || '';
        const cryptoPassword = loopCrypto(password, 2);
        const parsed = parseQuery();
        const { jwtLogin, return_to } = parsed;
        let params = {
          ...payload,
          password: cryptoPassword,
          loginType: '',
        };
        if (jwtLogin) {
          params.jwtLogin = !!jwtLogin;
        }
        const utm = getUtm();
        if (utm) {
          params = {
            ...params,
            ...utm,
          };
        }
        const res = yield call(services.login, {
          ...params,
        });

        kcsensorsManualTrack({
          kc_pageid: 'B1login',
          spm: ['B1login', ['submit', '1']],
        });
        const isNeedMailAuthorize = checkIsNeedMailAuthorize(res.data || {});
        yield put({
          onSuccess,
          trackResultParams,
          type: 'loginCallback',
          payload: { ...(res.data || {}), isShowMailAuthorizePage: isNeedMailAuthorize },
        });

        if (_loginType === PHONELOGINTABKEY) {
          // 手机登录
          localStorage.setItem(ACCOUNT_KEY[_loginType] || '', payload.phone || '');
          localStorage.setItem(ACCOUNT_KEY.mobileCode, payload.mobileCode || '');
        } else {
          localStorage.setItem(ACCOUNT_KEY[_loginType] || '', payload.account || '');
        }
      } catch (e) {
        if (e.code && e.code === CAPTCHA_CODE) {
          yield put({
            type: 'captchaInit',
            payload: {
              bizType,
              onCallBack: {
                type: 'login',
                payload,
                onSuccess,
                trackResultParams: trackResultParams || {},
              },
            },
          });
        } else {
          // 登录失败埋点
          kcsensorsManualTrack(
            {
              checkID: false,
              data: {
                ...trackResultParams,
                is_success: false,
                fail_reason: e.msg,
                fail_reason_code: e.code,
              },
            },
            'login_result',
          );
          yield put({ type: 'update', payload: { loginLoading: false } });
          throw e;
        }
      }
    },
    *validate({ payload, onSuccess, trackResultParams = {} }, { call, select, put }) {
      yield put({ type: 'update', payload: { gfaBtnLoading: true } });
      try {
        const { token, finishUpgrade, sendChannel } = yield select((state) => state[NAMESPACE]);
        const { validationType } = payload || {};
        const checkType = validationType === 'MY_SMS' ? sendChannel : validationType;
        const { data, success, msg: errorMsg, code: errorCode } = yield call(services.validate, {
          ...payload,
          token,
          validationType: checkType,
          anonymousId: getAnonymousID(),
        });
        if (success && data) {
          const _type = _loginType || ACCOUNTLOGINTABKEY;
          window.localStorage.setItem('kucoinv2_login_key', JSON.stringify(_type));
          // 登录成功，调用login，上报uid
          sensorsLogin(String(data.uid), String(data.honorLevel));
          // 登录成功埋点
          kcsensorsManualTrack(
            {
              checkID: false,
              data: {
                ...trackResultParams,
                is_success: true,
                is_login: true, // 客户端设置is_login可能在这个之前，故这里默认login true
              },
            },
            'login_result',
          );
          yield call(onSuccess, { ...data, finishUpgrade });
        } else {
          // 登录失败埋点
          kcsensorsManualTrack(
            {
              checkID: false,
              data: {
                ...trackResultParams,
                is_success: false,
                fail_reason: errorMsg,
                fail_reason_code: errorCode,
              },
            },
            'login_result',
          );
        }
      } catch (e) {
        // 登录失败埋点
        kcsensorsManualTrack(
          {
            checkID: false,
            data: {
              ...trackResultParams,
              is_success: false,
              fail_reason: e && e.msg,
              fail_reason_code: e && e.code,
            },
          },
          'login_result',
        );
        if (e.code && e.code === TOKEN_INVALID_CODE) {
          yield put({
            type: 'update',
            payload: {
              needValidations: [],
            },
          });
        }
        throw e;
      } finally {
        yield put({ type: 'update', payload: { gfaBtnLoading: false } });
      }
    },
    *sendSms({ payload }, { call, select, put }) {
      const { sendChannel } = payload;
      const { token } = yield select((state) => state[NAMESPACE]);
      yield put({ type: 'update', payload: { loadingSms: true } });
      try {
        const {
          data: { retryAfterSeconds },
        } = yield call(services.sendSms, { token, sendChannel });
        yield put({
          type: 'update',
          payload: {
            smsRetryAfterSeconds: {
              time: retryAfterSeconds,
            },
            sendChannel,
          },
        });
      } catch (e) {
        console.error(e);
        throw e;
      } finally {
        yield put({ type: 'update', payload: { loadingSms: false } });
      }
    },
    *getCountryCodes(_, { call, put }) {
      const { data } = yield call(services.getCountryCodes);
      yield put({
        type: 'update',
        payload: {
          countryCodes: data,
        },
      });
    },
    *validateAccount({ payload: { account } }, { call, put }) {
      try {
        const { data } = yield call(services.validateAccount, { account });
        yield put({
          type: 'update',
          payload: {
            accountDup: !data,
          },
        });
      } catch (e) {
        if (e.code && e.code === '4001') {
          yield put({
            type: 'update',
            payload: {
              accountDup: false,
            },
          });
        }
      }
    },
    // 初始化获取token
    *getToken(_, { put, call }) {
      const utm = getUtm();
      const parsed = parseQuery();
      const { jwtLogin } = parsed;
      let params = {};
      if (jwtLogin) {
        params.jwtLogin = !!jwtLogin;
      }
      if (utm) {
        params = {
          ...params,
          ...utm,
        };
      }
      const { data } = yield call(services.getToken, params);
      yield put({
        type: 'update',
        payload: {
          initToken: data.token,
        },
      });
    },
    *getStatus(_, { put, call, select }) {
      const { initToken } = yield select((state) => state[NAMESPACE]);
      const { data } = yield call(services.checkLoginStatus, { qrToken: initToken });
      // console.log(data);
      const { jwtToken } = data || {};
      if (jwtToken) {
        // 直接跳转jwt逻辑
        const parsed = parseQuery();
        const { return_to } = parsed;
        if (return_to) {
          if (!JUMP_JWT) {
            JUMP_JWT = true;
            window.location.href = getJWTPath('zendesk', jwtToken, return_to);
          }
          return;
        }
      }

      yield put({
        type: 'update',
        payload: {
          ...data,
        },
      });
    },
    *resetScan(__, { put }) {
      yield put({
        type: 'update',
        payload: {
          initToken: undefined,
          status: undefined,
          loginToken: undefined,
        },
      });
    },
    *validateToken(__, { call, select }) {
      const { loginToken } = yield select((state) => state[NAMESPACE]);
      yield call(services.validationLogin, {
        token: loginToken,
        validationType: 'QR_CODE',
      });
    },
    *resendMail(__, { call, put, select }) {
      const { verifyToken, verifyCheckToken } = yield select((state) => state[NAMESPACE]);
      const res = yield call(services.resendMail, { verifyToken, verifyCheckToken });
      yield put({
        type: 'update',
        payload: res.data,
      });
      return res;
    },
    *triggerPolling(__, { put }) {
      yield put({
        type: 'watchPolling',
        payload: { effect: 'getMailVerifyResult', interval: 5 * 1000 },
      });
    },
    *getMailVerifyResult(
      { payload: { onSuccess, trackResultParams = {} } },
      { put, call, select },
    ) {
      const { verifyToken, verifyCheckToken } = yield select((state) => state[NAMESPACE]);
      try {
        const { data } = yield call(services.getMailVerifyResult, {
          verifyToken,
          verifyCheckToken,
        });
        if (data?.status === 'success') {
          yield put({
            onSuccess,
            trackResultParams,
            type: 'loginCallback',
            payload: {
              ...(data?.loginInfo || {}),
              isShowMailAuthorizePage: !data?.loginInfo?.needValidations?.length,
            },
          });
        }
      } catch (e) {
        if (e?.code === MAIL_AUTHORIZE_EXPIRE_CODE) {
          yield put({ type: 'reset' });
        }
      }
    },
  },
});
